import { useEffect, useMemo, useRef, useState } from "react";
import { Icon, type IconName } from "@/components/ui/Icon";
import { useSpace } from "@/hooks/useSpace";
import { BIZ_META, SPACE_META, TENANT_BIZ, bizGate, isTenantKey, type BizKey, type SpaceKey } from "@/domain/space";
import { isHomeKey, menuPath, navFor } from "@/domain/menus";

/**
 * HeaderSearch — 헤더 오른쪽 검색 (B′). 지금은 "메뉴 찾기"만 한다.
 *
 * 열 수 있는 모든 공간의 메뉴(권한 통과분)에서 이름으로 찾고, 고르면 그 공간으로 전환 후 이동.
 * PC: 입력칸 상시. fold: 아이콘만 → 누르면 입력칸이 펼쳐짐(.is-open). MO: 헤더 오른쪽 자체를 숨김.
 * 단축키 ⌘K / Ctrl+K.
 */
interface Hit {
  space: SpaceKey;
  /** 사업 메뉴면 그 사업(법인 공통·내 업무는 null). 기본 사업이 아니면 결과에 사업 이름을 붙인다 */
  biz: BizKey | null;
  key: string;
  label: string;
  icon: IconName;
}

const MAX_HITS = 8;

export function HeaderSearch() {
  const { space, spaces, switchSpace, hasPermIn } = useSpace();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // 지금 공간을 먼저, 나머지 공간은 그 뒤
  const all = useMemo<Hit[]>(() => {
    const order = [space, ...spaces.filter((s) => s !== space)];
    const out: Hit[] = [];
    for (const sp of order) {
      // 법인은 사업마다(권한 있는 사업만) 모으되 같은 메뉴는 한 번만(법인 공통·양쪽 사업 공통 메뉴)
      const bizs: (BizKey | null)[] = isTenantKey(sp)
        ? TENANT_BIZ[sp].filter((b) => { const g = bizGate(b); return !g || hasPermIn(g, sp); })
        : [null];
      const seen = new Set<string>();
      for (const [i, b] of bizs.entries()) {
        for (const n of navFor(sp, b)) {
          if (n.type !== "item" || isHomeKey(n.key) || seen.has(n.key)) continue;
          // 공간별 권한 — 법인 칸은 그 법인 기준, 내 업무 칸은 법인 무관
          if (n.gate && !n.gate.some((k) => hasPermIn(k, sp === "my" ? undefined : sp))) continue;
          seen.add(n.key);
          const label = b && i > 0 ? `${BIZ_META[b].label} ${n.label}` : n.label;
          out.push({ space: sp, biz: b, key: n.key, label, icon: n.icon });
        }
      }
    }
    return out;
  }, [space, spaces, hasPermIn]);

  const term = q.trim().toLowerCase();
  const hits = useMemo(() => {
    if (!term) return [];
    return all
      .filter((h) => h.label.toLowerCase().includes(term) || SPACE_META[h.space].short.includes(term))
      .slice(0, MAX_HITS);
  }, [all, term]);

  // ⌘K / Ctrl+K
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // 바깥 클릭 닫기
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // ref 대신 activeElement 로 포커스 해제 — 결과 목록 map 안에서 부르는 함수가 ref 를 만지면 react-hooks/refs 오탐
  const close = () => {
    setOpen(false); setQ(""); setCursor(0);
    (document.activeElement as HTMLElement | null)?.blur();
  };
  const go = (h: Hit) => {
    close();
    switchSpace(h.space, { to: menuPath(h.key, h.space, h.biz) });
  };

  return (
    <div className={`hds${open ? " is-open" : ""}`} ref={wrapRef}>
      <button
        type="button"
        className="hd-icon-btn hds-icon"
        aria-label="메뉴 검색"
        onClick={() => { setOpen(true); requestAnimationFrame(() => inputRef.current?.focus()); }}
      >
        <Icon name="search" size={20} />
      </button>
      <label className="hds-field">
        <Icon name="search" size={16} />
        <input
          ref={inputRef}
          type="text"
          value={q}
          placeholder="메뉴 검색"
          aria-label="메뉴 검색"
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQ(e.target.value); setCursor(0); }}
          onKeyDown={(e) => {
            if (e.key === "Escape") { close(); return; }
            if (!hits.length) return;
            if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => (c + 1) % hits.length); }
            else if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => (c - 1 + hits.length) % hits.length); }
            else if (e.key === "Enter") { e.preventDefault(); go(hits[Math.min(cursor, hits.length - 1)]); }
          }}
        />
        <kbd>⌘K</kbd>
      </label>
      {open && term && (
        <div className="hds-pop" role="listbox" aria-label="메뉴 검색 결과">
          {hits.length === 0 ? (
            <div className="hds-empty">'{q.trim()}' 메뉴가 없습니다</div>
          ) : (
            hits.map((h, i) => (
              <button
                key={`${h.space}:${h.key}`}
                type="button"
                role="option"
                aria-selected={i === cursor}
                className={`hds-item${i === cursor ? " is-cursor" : ""}`}
                onMouseEnter={() => setCursor(i)}
                onClick={() => go(h)}
              >
                <span className="hds-item-ico"><Icon name={h.icon} size={16} /></span>
                <span className="hds-item-label">{h.label}</span>
                <span className={`hds-item-space ${h.space === "my" ? "sp-my" : `is-space-${h.space}`}`}>
                  {SPACE_META[h.space].short}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
